/* =============================================================================
   Piklo — the floating world.

   Pickle slices drawn in the label's own vocabulary (brine ring, chartreuse
   flesh, cream seeds) drifting behind the page at three depths, with
   carbonation rising through. Pure CSS-transform animation; the layer is fixed,
   pointer-transparent, and sits UNDER the content (z:-1 over the html gradient).

   PLACEMENT (rebuilt 2026-08-12): the slices used to be scattered by seeded
   random, which is how you get two of them touching and a bare patch beside
   them. They are now placed by dart-throwing against a hard minimum gap, in a
   band that keeps every slice whole and on screen — see place() below. It runs
   again on resize, because the constraint is measured in pixels.

   Respects prefers-reduced-motion (static, sparse) and narrow screens.
   No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  if (document.querySelector('.pkworld')) return;   // theme editor re-renders

  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Tuning. GAP is edge-to-edge, so two slices are never nearer than this. */
  var GAP = 16;          // px of clear space between any two slices, BEFORE the
                         // drift allowance is added to it in place()
  var EDGE = 20;         // px of clear space from the viewport edge
  var TRIES = 600;       // dart throws per slice before it shrinks
  var SHRINKS = 4;       // how many times a stubborn slice may shrink
  var SPEED_MIN = 6;     // px a second — a float, not a screensaver
  var SPEED_MAX = 17;
  var RESTITUTION = 0.92;// how bouncy a knock is
  var TOUCH = 6;         // px of air kept between two slices on contact
  var RECYCLE_PAD = 40;  // how far past the edge a slice goes before it re-enters

  /* ---- slice artwork: label-accurate ------------------------------------- */
  function sliceSVG() {
    // brine ring #7CBB3E, flesh #DCE685, seeds #F7F2EB — straight off the wrap
    var seeds = '';
    for (var i = 0; i < 5; i++) {
      var a = (i / 5) * Math.PI * 2 - Math.PI / 2;
      var sx = 50 + Math.cos(a) * 17, sy = 50 + Math.sin(a) * 17;
      var deg = (a * 180 / Math.PI + 90).toFixed(1);
      seeds += '<ellipse cx="' + sx.toFixed(1) + '" cy="' + sy.toFixed(1) +
               '" rx="5.2" ry="8.4" fill="#F7F2EB" transform="rotate(' + deg +
               ' ' + sx.toFixed(1) + ' ' + sy.toFixed(1) + ')"/>';
    }
    return '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">' +
           '<circle cx="50" cy="50" r="48" fill="#7CBB3E"/>' +
           '<circle cx="50" cy="50" r="38" fill="#DCE685"/>' + seeds + '</svg>';
  }

  /* ---- layer scaffolding -------------------------------------------------- */
  var world = document.createElement('div');
  world.className = 'pkworld';
  world.setAttribute('aria-hidden', 'true');
  var depths = [];
  for (var d = 0; d < 3; d++) {
    var layer = document.createElement('div');
    layer.className = 'pkworld__layer pkworld__layer--' + d;
    world.appendChild(layer);
    depths.push(layer);
  }
  document.body.prepend(world);

  /* Deterministic pseudo-random so every reload composes the same page — a
     seeded LCG. Math.random would reshuffle the design on every visit. */
  function makeRnd(s) {
    return function () { s = (s * 48271) % 2147483647; return s / 2147483647; };
  }

  /* ---- placement ----------------------------------------------------------
     Dart throwing, biggest first (small ones can always find a hole later).
     A candidate is accepted only if it clears every placed slice by GAP and
     sits fully inside the safe band, drift included. A slice that cannot find
     a spot shrinks and tries again; one that still cannot is dropped rather
     than allowed to overlap. */
  function place() {
    depths.forEach(function (l) { l.innerHTML = ''; });

    var W = innerWidth, H = innerHeight;
    var narrow = W < 760;
    var count = narrow ? 10 : (reduced ? 12 : 20);

    // the layer is inset:-8vh -4vw, so viewport (0,0) is here inside it
    var offX = W * 0.04, offY = H * 0.08;
    /* How far the wander carries a slice from where it was placed. The keyframes
       in piklo-v6.css reach ±ax horizontally and +ay / -0.55ay vertically, and
       breathe to 1.045 scale — every one of those numbers has to be paid for
       here, or the animation walks slices into each other and off the edges. */
    var ampX = W * 0.026, ampY = H * 0.043;
    var SCALE_MAX = 1.045;
    var driftX = ampX, driftY = ampY;
    var driftMag = Math.hypot(ampX, ampY);

    var rnd = makeRnd(20260812);
    var specs = [];
    for (var i = 0; i < count; i++) {
      var depth = i % 3;                        // 0 far … 2 near
      // sized off the viewport so the composition holds at any width
      var base = depth === 0 ? 0.105 : depth === 1 ? 0.062 : 0.036;
      var span = depth === 0 ? 0.045 : depth === 1 ? 0.026 : 0.016;
      specs.push({
        depth: depth,
        // floor it: on a phone the near layer worked out to 16px, which is a dot
        size: Math.max(28, Math.min(W, H) * (base + rnd() * span)),
        rot: rnd() * 360,
        dur: 22 + rnd() * 26,          // slow: a float, not a drift
        delay: -rnd() * 40,
        spin: rnd() < 0.5 ? 1 : -1,
        spinAmt: rnd(),
        // signed, so some circuit clockwise and some the other way
        wx: (rnd() < 0.5 ? -1 : 1) * (0.55 + rnd() * 0.45),
        wy: (rnd() < 0.5 ? -1 : 1) * (0.55 + rnd() * 0.45)
      });
    }
    specs.sort(function (a, b) { return b.size - a.size; });

    /* Dart-throw a valid start, then RELAX it.

       Two lessons are baked in here:

       1. GAP must exceed the distance two slices can drift TOWARD each other.
          The drift is ±1vw / ±2.2vh each and neighbours run on opposite phases,
          so they can close ~2 * hypot(driftX, driftY) between them. A 34px gap
          measured statically became a real overlap mid-animation.
       2. A jittered grid sounds more even and measured worse (spread of
          nearest-neighbour distance went from 0.57 to 1.02 of the mean): empty
          cells leave holes and confined cells force awkward positions.

       Pairwise repulsion does the job properly. Every pair closer than the
       target pushes apart a little, walls push back, repeat. It converges on an
       arrangement with no clumps and no bare patches. */
    // two neighbours can wander toward each other, and both can be breathing out
    var gap = GAP + 2 * driftMag;

    var placed = [];
    function nearestViolation(x, y, r, skip) {
      for (var j = 0; j < placed.length; j++) {
        if (j === skip) continue;
        var p = placed[j];
        if (Math.hypot(x - p.x, y - p.y) < (r + p.r) * SCALE_MAX + gap) return true;
      }
      return false;
    }

    specs.forEach(function (sp) {
      for (var shrink = 0; shrink <= SHRINKS; shrink++) {
        var r = sp.size / 2;
        var rs = r * SCALE_MAX;
        var minX = EDGE + driftX + rs, maxX = W - EDGE - driftX - rs;
        var minY = EDGE + driftY + rs, maxY = H - EDGE - driftY - rs;
        if (maxX > minX && maxY > minY) {
          for (var t = 0; t < TRIES; t++) {
            var x = minX + rnd() * (maxX - minX);
            var y = minY + rnd() * (maxY - minY);
            if (!nearestViolation(x, y, r, -1)) {
              placed.push({ x: x, y: y, r: r, sp: sp, minX: minX, maxX: maxX, minY: minY, maxY: maxY });
              return;
            }
          }
        }
        sp.size *= 0.86;     // no room at that size — try smaller
      }
      // still nowhere to go: leave it out rather than overlap something
    });

    for (var pass = 0; pass < 400; pass++) {
      for (var a = 0; a < placed.length; a++) {
        var pa = placed[a], mx = 0, my = 0;
        for (var bIdx = 0; bIdx < placed.length; bIdx++) {
          if (bIdx === a) continue;
          var pb = placed[bIdx];
          var dx = pa.x - pb.x, dy = pa.y - pb.y;
          var dist = Math.hypot(dx, dy) || 0.01;
          var want = (pa.r + pb.r) * SCALE_MAX + gap;
          /* ALWAYS repel, with a 1/d falloff, plus a hard term inside the gap.
             A weak long-range ATTRACTION huddles every
             slice into the middle of the screen — evenly spaced, but covering
             half the frame. Pure repulsion inside a box is what spreads points
             out to fill it. */
          var push = (want * want) / (dist * dist) * 1.6;
          if (dist < want) push += (want - dist) * 0.5;
          mx += (dx / dist) * push;
          my += (dy / dist) * push;
        }
        pa.x = Math.min(pa.maxX, Math.max(pa.minX, pa.x + mx * 0.35));
        pa.y = Math.min(pa.maxY, Math.max(pa.minY, pa.y + my * 0.35));
      }
    }

    // relaxation is a soft process: shrink anything it could not fully separate
    for (var g = 0; g < 40; g++) {
      var clean = true;
      for (var q = 0; q < placed.length; q++) {
        if (nearestViolation(placed[q].x, placed[q].y, placed[q].r, q)) {
          placed[q].r *= 0.94; clean = false;
        }
      }
      if (clean) break;
    }

    /* ---- hand the placed set to the simulation --------------------------- */
    bodies = placed.map(function (p) {
      var sp = p.sp, sz = p.r * 2;
      var el = document.createElement('div');
      el.className = 'pkslice pkslice--d' + sp.depth;
      el.style.width = el.style.height = sz.toFixed(0) + 'px';
      el.style.left = '0px';
      el.style.top = '0px';
      el.innerHTML = sliceSVG();
      depths[sp.depth].appendChild(el);
      // near slices drift a little faster than far ones — it reads as depth
      var speed = (SPEED_MIN + rnd() * (SPEED_MAX - SPEED_MIN)) * (0.6 + sp.depth * 0.3);
      var dir = rnd() * Math.PI * 2;
      return {
        el: el, r: p.r, x: p.x, y: p.y,
        vx: Math.cos(dir) * speed,
        vy: Math.sin(dir) * speed,
        rot: sp.rot,
        spin: (rnd() < 0.5 ? -1 : 1) * (2 + rnd() * 7),   // degrees a second
        m: p.r * p.r                                      // mass by area
      };
    });

    view = { W: W, H: H, offX: offX, offY: offY };
    draw();                       // paint the opening frame straight away
    /* The simulation waits for the hero entrance. Starting a 60fps loop over 20
       elements while the headline, the ribbon draw and the can reveal are all
       animating is exactly the kind of contention that reads as "it lags for a
       second". The field is already composed and visible; it just is not moving
       yet. */
    if (!reduced) setTimeout(start_, 1500);
  }

  /* ==========================================================================
     The simulation.

     Slow drifting circles that bounce off each other and RECYCLE at the edges:
     one that leaves re-enters from the opposite side at a fresh offset, so the
     field is always arriving rather than merely oscillating. Collisions are
     elastic, mass by area, with a positional correction so two slices never sit
     inside one another even for a frame.

     20 bodies is 190 pair tests a frame — nothing. The loop stops when the tab
     is hidden and never runs at all under prefers-reduced-motion.
     ====================================================================== */
  var bodies = [], view = { W: 0, H: 0, offX: 0, offY: 0 }, raf = 0, last = 0;
  /* the loop needs its own RNG: `rnd` lives inside place() and the recycle
     branch below runs long after place() has returned */
  var simRnd = makeRnd(778291);

  function draw() {
    for (var i = 0; i < bodies.length; i++) {
      var b = bodies[i];
      b.el.style.transform =
        'translate3d(' + (view.offX + b.x - b.r).toFixed(1) + 'px,' +
                         (view.offY + b.y - b.r).toFixed(1) + 'px,0) rotate(' + b.rot.toFixed(1) + 'deg)';
    }
  }

  function step(now) {
    raf = requestAnimationFrame(step);
    var dt = Math.min(0.05, (now - last) / 1000 || 0.016);
    last = now;

    var i, j, b;
    for (i = 0; i < bodies.length; i++) {
      b = bodies[i];
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.rot += b.spin * dt;

      // leave one side, arrive on the other — the field keeps renewing
      var m = b.r + RECYCLE_PAD;
      if (b.x < -m) { b.x = view.W + m; b.y = m + simRnd() * (view.H - 2 * m); }
      else if (b.x > view.W + m) { b.x = -m; b.y = m + simRnd() * (view.H - 2 * m); }
      if (b.y < -m) { b.y = view.H + m; b.x = m + simRnd() * (view.W - 2 * m); }
      else if (b.y > view.H + m) { b.y = -m; b.x = m + simRnd() * (view.W - 2 * m); }
    }

    // elastic collisions, mass by area
    for (i = 0; i < bodies.length; i++) {
      for (j = i + 1; j < bodies.length; j++) {
        var a = bodies[i], c = bodies[j];
        var dx = c.x - a.x, dy = c.y - a.y;
        var d = Math.hypot(dx, dy);
        var min = a.r + c.r + TOUCH;
        if (d === 0 || d >= min) continue;

        var nx = dx / d, ny = dy / d;

        // push them apart first, so they never render intersecting
        var overlap = (min - d) / 2;
        var totalM = a.m + c.m;
        a.x -= nx * overlap * (c.m / totalM) * 2;
        a.y -= ny * overlap * (c.m / totalM) * 2;
        c.x += nx * overlap * (a.m / totalM) * 2;
        c.y += ny * overlap * (a.m / totalM) * 2;

        // then exchange momentum along the normal
        var rvx = c.vx - a.vx, rvy = c.vy - a.vy;
        var sep = rvx * nx + rvy * ny;
        if (sep > 0) continue;                       // already separating
        var imp = -(1 + RESTITUTION) * sep / (1 / a.m + 1 / c.m);
        a.vx -= (imp / a.m) * nx; a.vy -= (imp / a.m) * ny;
        c.vx += (imp / c.m) * nx; c.vy += (imp / c.m) * ny;

        // a knock sets them turning
        a.spin += sep * 0.05; c.spin -= sep * 0.05;
      }
    }

    // keep the drift gentle: nudge every body back toward its cruising speed
    for (i = 0; i < bodies.length; i++) {
      b = bodies[i];
      var sp = Math.hypot(b.vx, b.vy);
      if (sp > SPEED_MAX * 1.6) { var k = (SPEED_MAX * 1.6) / sp; b.vx *= k; b.vy *= k; }
      else if (sp < SPEED_MIN * 0.5 && sp > 0) { var k2 = (SPEED_MIN * 0.5) / sp; b.vx *= k2; b.vy *= k2; }
      if (Math.abs(b.spin) > 26) b.spin *= 0.9;
    }

    draw();
  }

  function start_() {
    cancelAnimationFrame(raf);
    last = performance.now();
    draw();
    raf = requestAnimationFrame(step);
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) cancelAnimationFrame(raf);
    else if (!reduced && bodies.length) start_();
  });

  place();

  var resizeTimer = 0;
  addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      cancelAnimationFrame(raf);
      place();                                 // sizes and bounds are in pixels
    }, 220);
  }, { passive: true });

  /* ---- rising carbonation (skip when reduced) ----------------------------- */
  if (!reduced) {
    var fizzRnd = makeRnd(90210);
    var fizz = document.createElement('div');
    fizz.className = 'pkworld__layer pkworld__fizz';
    for (var b = 0; b < (innerWidth < 760 ? 8 : 18); b++) {
      var bub = document.createElement('span');
      bub.className = 'pkbub';
      var bs = 4 + fizzRnd() * 12;
      bub.style.width = bub.style.height = bs.toFixed(0) + 'px';
      bub.style.left = (fizzRnd() * 98).toFixed(1) + 'vw';
      bub.style.animationDuration = (9 + fizzRnd() * 14).toFixed(1) + 's';
      bub.style.animationDelay = (-fizzRnd() * 20).toFixed(1) + 's';
      fizz.appendChild(bub);
    }
    world.appendChild(fizz);
  }

  /* No scroll parallax. It looked good for one screen and then dragged the near
     layer 1,300px off the top of an 11,000px page — every slice near an edge
     ended up sliced itself. The drift animation carries the life instead. */
})();
